/** All data about one performed set. Used both in the intake form and in regular workout tracking.
 * Some data is just recorded, some is calculated from it.
 */
export interface SetStats {
  // actually recorded values...
  weight_kg?: number; // this is "added weight"
  weight_unit?: string;
  reps?: number; // actually done
  bw_pc?: number; // how much of bw was added to the weight (depends on the exercise)

  // calculated values...
  /** Total weight including bw according to the percentage - note that we don't store bw itself, but it can be deduced if needed. */
  weight_net_kg?: number;
  /** 1RM with BW included. */
  estimated1rm_net_kg?: number;
  /** Added weight of 1RM (without BW). */
  estimated1rm_kg?: number; // excluding bw back
}

/** Calculate 1RM directly from weight and reps without modifying any object. */
export function calculate1RM(weight_kg: number, reps: number, bodyWeight_kg: number, bw_pc: number = 0): { estimated1rm_net_kg: number; estimated1rm_kg: number } {
  reps = Math.round(reps);
  if (reps <= 0 || isNaN(weight_kg)) {
    return { estimated1rm_net_kg: 0, estimated1rm_kg: 0 };
  }

  // add bodyweight
  bodyWeight_kg = bodyWeight_kg || 0;
  const weight_net_kg = weight_kg + bodyWeight_kg * (bw_pc / 100);

  // calculate 1rm including bodyweight
  let estimated1rm_net_kg: number;
  if (reps === 1) {
    estimated1rm_net_kg = weight_net_kg;
  } else {
    const part1 = weight_net_kg / (1.0278 - 0.0278 * reps);
    const part2 = weight_net_kg * (1 + reps / 30);
    const part3 = (100 * weight_net_kg) / (48.8 + 53.8 * Math.exp(-0.075 * reps));
    estimated1rm_net_kg = (part1 + part2 + part3) / 3;
  }

  // subtract bodyweight back out of it
  const estimated1rm_kg = estimated1rm_net_kg - bodyWeight_kg * (bw_pc / 100);

  return { estimated1rm_net_kg, estimated1rm_kg };
}

/**
 * The added weight at which `reps` reps would produce this 1RM — `calculate1RM` run backwards.
 * Used when a set beat its rep target and the app needs the load that target really corresponds to
 * (#4165): a user asked for their 8RM who logs 30 kg x 20 has told us it is nearer 43 kg.
 *
 * The rep factor comes from calling `calculate1RM` with a load of exactly 1 rather than repeating
 * its three formulas here, so the two directions cannot drift apart.
 *
 * Returns `undefined` rather than NaN when the inputs cannot produce an answer, so a caller has to
 * decide what to do instead of propagating a broken weight.
 */
export function estimateWeightForReps(
  estimated1rm_net_kg: number,
  reps: number,
  bodyWeight_kg: number,
  bw_pc: number = 0,
): number | undefined {
  // if the inputs cannot produce a weight, say so rather than returning a NaN
  if (!Number.isFinite(estimated1rm_net_kg) || estimated1rm_net_kg <= 0 || !Number.isFinite(reps)) {
    return undefined;
  }
  reps = Math.round(reps);
  if (reps <= 0) {
    return undefined;
  }

  // how many times the lifted load the 1RM is, for a set taken to this many reps
  const oneRmFactor = calculate1RM(1, reps, 0, 0).estimated1rm_net_kg;
  if (!Number.isFinite(oneRmFactor) || oneRmFactor <= 0) {
    return undefined;
  }

  // work in net terms (the 1RM includes the bodyweight share), then take the bodyweight back out
  // so the answer is an ADDED weight, matching what target_weight_kg holds
  bodyWeight_kg = bodyWeight_kg || 0;
  const bwLoad_kg = bodyWeight_kg * (bw_pc / 100);
  return estimated1rm_net_kg / oneRmFactor - bwLoad_kg;
}

/** Perform strength estimation calculations - producing calculated values from recorded values in the SetStats, takes into account user's bodyweight depending on percentage defined by the exercise. */
export function estimate1RM(stats: SetStats, bodyWeight_kg: number) {
  // we will recalculate these - or they need to be removed if some inputs have become invalid
  delete stats.weight_net_kg;
  delete stats.estimated1rm_net_kg;
  delete stats.estimated1rm_kg;

  stats.reps = Math.round(stats.reps); // just in case
  // if there is no usable weight, leave the figures deleted. `Number.isFinite` rather than `isNaN`:
  // `isNaN(null)` is false, so a null weight used to pass and be estimated as 0 kg lifted
  // (df-1c8964c3e614). A real 0 kg (a bodyweight exercise with nothing added) is still estimated.
  if (stats.reps <= 0 || !Number.isFinite(stats.weight_kg)) {
    return stats;
  }

  let bw_pc = stats.bw_pc || 0;

  // add bodyweight
  bodyWeight_kg = bodyWeight_kg || 0; // guard against undefined values
  stats.weight_net_kg = stats.weight_kg + bodyWeight_kg * (bw_pc / 100);

  // Use the new calculate1RM function for the actual calculation
  const { estimated1rm_net_kg, estimated1rm_kg } = calculate1RM(stats.weight_kg, stats.reps, bodyWeight_kg, bw_pc);
  stats.estimated1rm_net_kg = estimated1rm_net_kg;
  stats.estimated1rm_kg = estimated1rm_kg;

  return stats;
}
